import {
    Grid,
    Subheader,
    Button,
    Flex,
    Label,
    useStyles,
    pxToRem,
    Avatar,
} from '@archipro-design/aria';
import * as S from './ContactPage.desktop.style';
import type {
    ContactLoaderData,
    LocationDataType,
    ProfessionalInitialType,
} from '@modules/professional/type/contact-page-types';
import { MapPinFill } from '@archipro-design/icons';
import { CloseLine } from '@archipro-design/icons';
import { Divider } from './Divider';
import ClickToReveal from '@modules/root/component/click-to-reveal/ClickToReveal';
import { parsePhone } from '~/modules/root/util/phone';
import {
    getFullAddress,
    getLocationSuburbText,
    getLocationProfessional,
} from '@modules/professional/util/contact-page-helper';
import { useLoaderData, useNavigate } from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useFormatDistance } from '~/modules/location/hook/use-format-distance';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';

interface ItemProps {
    location: LocationDataType;
    showCloseButton?: boolean;
    onLocationDetailClose: () => void;
    professional: ProfessionalInitialType;
    disableEnquire?: boolean;
    disableContactViewProfile?: boolean;
    autoRevealAddress?: boolean;
}

export const LocationDetail: React.FC<ItemProps> = ({
    location,
    professional,
    showCloseButton = true,
    onLocationDetailClose,
    disableEnquire,
    disableContactViewProfile,
}) => {
    const { css } = useStyles();
    const formatDistance = useFormatDistance();
    const navigate = useNavigate();
    const loaderData = useLoaderData<ContactLoaderData>();
    const tracker = useTracker();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    const onViewProfile = (
        e: React.SyntheticEvent<HTMLElement, Event>,
        routePath: string,
    ) => {
        e.preventDefault();

        tracker.log('ProfessionalLocationFranchiseClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: location.ID,
            },
        });

        navigate(routePath);
    };

    const locationProfessional = getLocationProfessional(
        location,
        professional,
        linkedProfiles,
    );

    const phoneParsed = location.Phone
        ? parsePhone(location.Phone, location.Country)
        : null;

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        includeBranch: true,
    });
    const { showEnquiryCTA, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);
    const enquiryLabel = 'Enquire';

    return (
        <>
            <Grid
                columns={`${pxToRem(72)} 1fr`}
                className={css(S.LocationListDetailContainer)}
            >
                <div>
                    <Avatar
                        image={{
                            src: locationProfessional.CompanyLogo ?? '',
                            alt: `${locationProfessional.Title} company logo`,
                        }}
                        size={72}
                        shape={'square'}
                        imageFit={'contain'}
                        variables={{
                            avatarImageBackgroundColor:
                                locationProfessional.LogoBackgroundColor,
                            borderWidth: '0',
                            avatarBackgroundColor: 'unset',
                        }}
                    />
                </div>
                <div className={css(S.LocationListDetail)}>
                    <Flex hAlign="start" space="between">
                        <div>
                            <Subheader variant={'04'} weight={'medium'}>
                                {location.Title}
                            </Subheader>

                            <Label variant="03">
                                {getLocationSuburbText(location)}
                            </Label>
                        </div>
                        {showCloseButton && (
                            <Button
                                onClick={() => onLocationDetailClose()}
                                transparent={true}
                                icon={<CloseLine size="2x" />}
                                size={18}
                                variables={{
                                    buttonPadding: 0,
                                    iconPadding: 0,
                                }}
                            />
                        )}
                    </Flex>

                    <Grid columns={2}>
                        <Label variant="04">Location type</Label>
                        <Label variant="04">{location.Type}</Label>

                        <Label variant="04">Address</Label>
                        <ClickToReveal
                            fulltext={getFullAddress(location)}
                            coordinates={location.MapCoordinates}
                            type={'Location'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-contact-accordion-address-reveal'
                            }
                            revealKey={`professional-${professional.ID}`}
                            revealSubKey={location.ID.toString()}
                        />

                        <Label variant="04">Phone</Label>

                        <ClickToReveal
                            fulltext={location.Phone || ''}
                            telHref={phoneParsed?.telUri}
                            type={'Phone'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-library-location-phone-reveal'
                            }
                            revealKey={`professional-${professional.ID}-${location.ID}`}
                            revealSubKey={location.ID.toString()}
                        />
                        {location.AllowsPickup && (
                            <>
                                <Label variant="04">Online orders</Label>
                                <Label variant="04">
                                    Click & Collect Available
                                </Label>
                            </>
                        )}
                    </Grid>

                    <Flex hAlign="end" space={'between'}>
                        <div>
                            <Button
                                as={'a'}
                                href={`/professional/${locationProfessional.URLSegment}`}
                                color="dark"
                                transparent={true}
                                size={16}
                                variables={{
                                    buttonPadding: 0,
                                }}
                                disabled={disableContactViewProfile}
                                onClick={(e) =>
                                    onViewProfile(
                                        e,
                                        `/professional/${locationProfessional.URLSegment}`,
                                    )
                                }
                            >
                                View Profile
                            </Button>
                        </div>
                        <Flex>
                            {location.distance && (
                                <Button
                                    size={16}
                                    transparent={true}
                                    icon={<MapPinFill size="1x" />}
                                    className={css(S.LocationListDistance)}
                                >
                                    {formatDistance(location.distance)}
                                </Button>
                            )}

                            {showEnquiryCTA && (
                                <Button
                                    size={14}
                                    outlined={true}
                                    disabled={disableEnquire}
                                    className={css(
                                        S.LocationListDetailEnquiryButton,
                                    )}
                                    onClick={() =>
                                        openEnquiryModal({
                                            title: enquiryLabel,
                                            ctaLabel: enquiryLabel,
                                        })
                                    }
                                >
                                    {enquiryLabel}
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                    {showSpecifierLoginPrompt && (
                        <div className="mt-4">
                            <SpecifierLoginPrompt
                                professionalName={professional.Title}
                                itemDescription="this professional"
                                showBottomBorder={false}
                                className="py-0"
                            />
                        </div>
                    )}
                </div>
            </Grid>
            <Divider />
        </>
    );
};
